import { observer } from "mobx-react-lite";
import React from "react";
import HeaderRow from "./components/HeaderRow";
import SelectedFilters from "./components/SelectedFilters";
import TabSelection from "./features/TabSelection";
import SearchResults from "./features/SearchResults";
import { productSearchStore as store } from "./ProductSearchStore";
import { sidebarMenuStore } from "features/SidebarMenu/SidebarMenuStore";
import ProductDetailsModal from "features/ProductDetailsModal";
import { searchStore } from "features/ProductSearch/SearchStore";
import RemoveConfirmModal from "./components/RemoveConfirmModal";
import {
  FetchReportTileReportKeys,
  InventoryPhysicalObjectDetailOut,
  LicenseAppType,
  ProductSourceEnum,
  SearchMode,
  SingleItem,
} from "api-client";
import clsx from "clsx";
import useFeatureFlag from "hooks/useFeatureFlag";

export interface ProductSearchProps {
  currentRow?: InventoryPhysicalObjectDetailOut | SingleItem;
  matchProductAndUpdate?: (
    product_id: string,
    current_row: SingleItem,
    detail_view_report_keys?: FetchReportTileReportKeys
  ) => Promise<void>;
  isDetailVeiw?: boolean;
  refreshAfterMatching?: () => Promise<void>;
  isManufacturerLicence?: boolean;
  closeModal?: () => void;
}

const AdvancedProductSearch = ({ ...props }: ProductSearchProps) => {
  const [loading, setLoading] = React.useState(true);
  const { isFeatureVisible } = useFeatureFlag();

  React.useEffect(() => {
    store.setSearchMode(
      isFeatureVisible("product_search_raw_mode") ? SearchMode.Raw : undefined
    );
  }, [isFeatureVisible("product_search_raw_mode")]);

  async function init() {
    setLoading(true);
    if (!sidebarMenuStore.currentOrganisation?.id) return;
    store.setCurrentObject(props.currentRow as SingleItem);

    const isManufacturerLicence =
      sidebarMenuStore.getCurrentLicense() === LicenseAppType.Manufacturer;

    if (isManufacturerLicence) {
      store.setIsManufacturerLicence(isManufacturerLicence);
      store.updateSearchInput("product_source", ProductSourceEnum.Custom);
    }

    await store.searchProducts();
    setLoading(false);
  }

  React.useEffect(() => {
    init();
  }, [sidebarMenuStore.currentOrganisation?.id]);

  React.useEffect(() => {
    return () => store.reset();
  }, []);

  const openDetails = searchStore.openDetails;

  return (
    <>
      {loading ? (
        skeleton()
      ) : (
        <div
          className={clsx({
            "pointer-events-none opacity-50": store.productSearchLoading,
          })}
        >
          <HeaderRow />
          <SelectedFilters />
          <TabSelection />
        </div>
      )}

      <SearchResults
        matchProductAndUpdate={props.matchProductAndUpdate}
        currentRow={props.currentRow}
        {...props}
      />

      <RemoveConfirmModal />

      <ProductDetailsModal
        productId={openDetails.product?.id}
        productName={openDetails.product?.name}
        productType={openDetails.product?.type}
        open={openDetails.open}
        close={() =>
          searchStore.setOpenDetails({ open: false, product: undefined })
        }
        isPublic={openDetails.product?.is_public}
      />
    </>
  );
};

export default observer(AdvancedProductSearch);

function skeleton() {
  return (
    <div className="animate-pulse px-0">
      <div className="h-10 w-full bg-gray-200 rounded dark:bg-gray-700 mb-5"></div>
      <div className="h-9 w-full bg-gray-200 rounded dark:bg-gray-700 mb-6"></div>
    </div>
  );
}
